import { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { NavBar } from "@/components/NavBar";
import { ConfidenceLabel } from "@/components/ConfidenceLabel";
import { MatrixActivity } from "@/components/MatrixActivity";
import { MethodologyBanner } from "@/components/MethodologyBanner";
import { FirstRunCard } from "@/components/UxFlows";
import { Button } from "@/design";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { useWebSocket } from "@/hooks/useWebSocket";
import { T, LSM_BANDS, AGE_BANDS } from "@/lib/theme";
import {
  OnboardingWidget, RateCardTab, ScheduleTab, ProfileTab, AdLogTab,
} from "@/pages/StationDashSetupTabs";

const C = T.dark;
// Amber accent for USSD listener-panel weighted data points (Wilson CI)
const PANEL_AMBER = "#F59E0B";
const TABS = [
  { id: "overview",  label: "Overview" },
  { id: "audience",  label: "Audience" },
  { id: "market",    label: "Market" },
  { id: "embed",     label: "Share Widget" },
  { id: "rate_card", label: "Rate Card" },
  { id: "schedule",  label: "Schedule" },
  { id: "profile",   label: "Station Profile" },
  { id: "ad_log",    label: "Ad Log" },
];

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  // First non-null payload point exposes panel CI metadata when applicable.
  const ref = payload.find((p) => p.payload)?.payload || {};
  const isPanel = ref.data_source === "PANEL";
  return (
    <div style={{ background: C.surface, border: `1px solid ${isPanel ? PANEL_AMBER + "55" : C.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
      <div style={{ color: C.muted, marginBottom: 5 }}>{label}</div>
      {payload.filter((p) => p.value != null).map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 600 }}>{p.name}: {(p.value || 0).toLocaleString()}</div>
      ))}
      {isPanel && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.border}`, color: PANEL_AMBER, fontSize: 11 }}>
          <div style={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 9, marginBottom: 3 }}>USSD Panel</div>
          {ref.panel_responses != null && <div style={{ color: C.muted }}>{ref.panel_responses.toLocaleString()} responses · ±{ref.margin_of_error_pct}%</div>}
          {ref.confidence_interval && (
            <div style={{ color: C.muted }}>
              95% CI: {ref.confidence_interval.lower.toLocaleString()}–{ref.confidence_interval.upper.toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function LiveCounter({ value, isOnline, source, loading }) {
  const [displayed, setDisplayed] = useState(value);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const diff = value - displayed;
    if (Math.abs(diff) < 3) { setDisplayed(value); return; }
    const t = setTimeout(() => setDisplayed((d) => d + Math.ceil(diff / 6)), 60);
    return () => clearTimeout(t);
  }, [value, displayed]);

  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 800); return () => clearInterval(t); }, []);

  if (loading) {
    return (
      <div data-testid="live-counter-skeleton" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "32px 36px" }}>
        <div style={{ width: 120, height: 12, background: C.border, borderRadius: 6, marginBottom: 18,
          animation: "shimmer 1.6s linear infinite", backgroundImage: `linear-gradient(90deg, ${C.border}, ${C.surface}, ${C.border})`, backgroundSize: "200% 100%" }} />
        <div style={{ width: 220, height: 60, background: C.border, borderRadius: 8,
          animation: "shimmer 1.6s linear infinite", backgroundImage: `linear-gradient(90deg, ${C.border}, ${C.surface}, ${C.border})`, backgroundSize: "200% 100%" }} />
        <div style={{ width: 180, height: 10, background: C.border, borderRadius: 6, marginTop: 14,
          animation: "shimmer 1.6s linear infinite", backgroundImage: `linear-gradient(90deg, ${C.border}, ${C.surface}, ${C.border})`, backgroundSize: "200% 100%" }} />
        <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      </div>
    );
  }

  return (
    <div data-testid="live-counter" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "32px 36px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: isOnline ? `linear-gradient(90deg,transparent,${C.cyan},transparent)` : `linear-gradient(90deg,transparent,#EF4444,transparent)` }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: isOnline ? "#10B981" : "#EF4444",
              display: "inline-block", animation: isOnline ? "pulseG 1.8s ease-out infinite" : "none" }} />
            <span style={{ color: C.muted, fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {isOnline ? "LIVE NOW" : "OFFLINE"}
            </span>
            <ConfidenceLabel source={source || "MODEL"} />
          </div>
          <div data-testid="live-counter-value" style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: "clamp(48px,7vw,80px)", fontWeight: 800,
            color: isOnline ? C.cyan : C.muted, lineHeight: 1, textShadow: isOnline ? `0 0 60px ${C.cyan}44` : "none" }}>
            {displayed.toLocaleString()}
          </div>
          <div style={{ color: C.muted, fontSize: 14, marginTop: 8 }}>estimated individual listeners right now</div>
          <div style={{ marginTop: 18, fontSize: 12, color: C.muted }}>
            Updated {new Date().toLocaleTimeString("en-GH")} · People, not sets
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80, alignSelf: "center" }}>
          {Array.from({ length: 18 }, (_, i) => {
            const h = 12 + Math.abs(Math.sin((i + tick * 0.25) * 0.45)) * 70;
            const active = i > 10;
            return (
              <div key={i} style={{
                width: 7, height: `${h}%`, borderRadius: 3, transition: "height 0.4s ease",
                background: active ? `linear-gradient(to top,${C.cyan},${C.cyan}55)` : C.border,
                opacity: active ? 1 : 0.3,
              }} />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function StationDash() {
  const [tab, setTab] = useState("overview");
  const [station, setStation] = useState(null);
  const [live, setLive] = useState(null);
  const [history, setHistory] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [allStations, setAllStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(null);

  // Fetch onboarding status — used by Overview widget + setup-tab refresh
  const refreshOnboarding = useCallback((stationId) => {
    if (!stationId) return;
    api.get(`/stations/${stationId}/onboarding-status`)
      .then((r) => setOnboarding(r.data))
      .catch(() => {});
  }, []);

  const load = useCallback(async (stationId) => {
    try {
      const [stRes, rtRes, histRes] = await Promise.all([
        api.get(`/stations/${stationId}`),
        api.get(`/stations/${stationId}/realtime`),
        api.get(`/stations/${stationId}/history?hours=24`),
      ]);
      setStation(stRes.data);
      setLive(rtRes.data);
      setHistory((histRes.data.history || []).map((h) => {
        const isPanel = h.data_source === "PANEL";
        return {
          time: new Date(h.timestamp).toLocaleTimeString("en-GH", { hour: "2-digit", minute: "2-digit" }),
          // Stream-side series — null on PANEL rows so the cyan line breaks cleanly
          people:      isPanel ? null : (h.estimated_total_people || 0),
          connections: isPanel ? null : (h.raw_connections || 0),
          // PANEL series — null on DIRECT/HYBRID/MODEL so amber line only renders on USSD-weighted points
          panel:               isPanel ? (h.estimated_total_people || 0) : null,
          data_source:         h.data_source,
          panel_responses:     h.panel_responses,
          margin_of_error_pct: h.margin_of_error_pct,
          confidence_interval: h.confidence_interval,
          label: new Date(h.timestamp).toLocaleTimeString("en-GH", { hour: "2-digit", minute: "2-digit" }),
        };
      }));
      const rankRes = await api.get(`/ratings/country/${encodeURIComponent(stRes.data.country)}`);
      setRankings(rankRes.data.rankings || []);
      refreshOnboarding(stationId);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [refreshOnboarding]);

  // Initial load — pick claimed station OR show picker
  useEffect(() => {
    (async () => {
      try {
        const my = await api.get("/auth/my-stations");
        const claimedId = my.data.stations?.[0]?.station_id;
        if (claimedId) {
          await load(claimedId);
        } else {
          // Show picker — load all stations and let user choose
          const all = await api.get("/stations?limit=100");
          setAllStations(all.data.stations || []);
          setLoading(false);
        }
      } catch (e) { setLoading(false); }
    })();
  }, [load]);

  // Real-time push via WebSocket — replaces the old 60s polling cycle.
  // History + rankings still refresh every 5 min (slower-moving data).
  useWebSocket(station ? [`station:${station.id}`] : [], (msg) => {
    if (msg.topic === `station:${station.id}` && msg.data) {
      setLive(msg.data);
    }
  });

  useEffect(() => {
    if (!station) return;
    const t = setInterval(async () => {
      try {
        const [histRes, rankRes] = await Promise.all([
          api.get(`/stations/${station.id}/history?hours=24`),
          api.get(`/ratings/country/${encodeURIComponent(station.country)}`),
        ]);
        setHistory((histRes.data.history || []).map((h) => {
          const isPanel = h.data_source === "PANEL";
          return {
            time: new Date(h.timestamp).toLocaleTimeString("en-GH", { hour: "2-digit", minute: "2-digit" }),
            people:      isPanel ? null : (h.estimated_total_people || 0),
            connections: isPanel ? null : (h.raw_connections || 0),
            panel:               isPanel ? (h.estimated_total_people || 0) : null,
            data_source:         h.data_source,
            panel_responses:     h.panel_responses,
            margin_of_error_pct: h.margin_of_error_pct,
            confidence_interval: h.confidence_interval,
            label: new Date(h.timestamp).toLocaleTimeString("en-GH", { hour: "2-digit", minute: "2-digit" }),
          };
        }));
        setRankings(rankRes.data.rankings || []);
      } catch {}
    }, 300000); // 5 min — history doesn't change minute-to-minute
    return () => clearInterval(t);
  }, [station]);

  const demo = live?.demographics || {};
  const gender = demo.gender || {};
  const lsm = demo.lsm || {};
  const age = demo.age || {};
  const total = live?.estimated_total_people || 0;
  // Latest snapshot — used to decorate stats with the amber PANEL badge when
  // the most recent USSD-weighted snapshot is fresher than the stream signal.
  const latestSnap = history.length ? history[history.length - 1] : null;
  const isPanelLatest = latestSnap?.data_source === "PANEL";

  if (loading) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&family=Inter:wght@400;500;600&display=swap');
          @keyframes pulseG{0%{box-shadow:0 0 0 0 #10B98199}70%{box-shadow:0 0 0 8px #10B98100}100%{box-shadow:0 0 0 0 #10B98100}}`}</style>
        <div style={{ color: C.muted, fontFamily: "Inter,sans-serif", fontSize: 16 }} data-testid="dashboard-loading">Loading your dashboard...</div>
      </div>
    );
  }

  if (!station) {
    // Pick-a-station screen — for unclaimed users OR to preview any station before claiming
    const byCountry = allStations.reduce((acc, s) => {
      (acc[s.country] = acc[s.country] || []).push(s);
      return acc;
    }, {});
    return (
      <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "Inter,sans-serif" }}>
        <NavBar dark={true} />
        <div style={{ maxWidth: 880, margin: "60px auto", padding: "0 24px" }} data-testid="no-station">
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📻</div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 8 }}>
              Welcome to WaveScorePro
            </h2>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.6, maxWidth: 520, margin: "0 auto 24px" }}>
              Two steps to see your station&apos;s live audience data.
            </p>

            {/* Onboarding steps */}
            <div style={{ display: "inline-flex", flexDirection: "column", gap: 10,
              textAlign: "left", marginBottom: 28, maxWidth: 380, width: "100%" }}>
              {[
                { done: true,  step: "1", label: "Account created",
                  sub: "You are in." },
                { done: false, step: "2", label: "Claim your station",
                  sub: "Search for your station and verify ownership. Takes 2 minutes.",
                  cta: { label: "Claim your station →", href: "/claim" } },
                { done: false, step: "3", label: "Live data appears here",
                  sub: "Listener count, demographics, and market ranking — updated every 60 seconds." },
              ].map((item) => (
                <div key={item.step} style={{ display: "flex", gap: 14, alignItems: "flex-start",
                  background: C.surface, border: `1px solid ${item.done ? C.cyan + "33" : C.border}`,
                  borderRadius: 10, padding: "14px 16px" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    background: item.done ? C.cyan : C.border,
                    color: item.done ? C.bg : C.muted,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 800 }}>
                    {item.done ? "✓" : item.step}
                  </div>
                  <div>
                    <div style={{ color: item.done ? C.cyan : C.text, fontWeight: 700,
                      fontSize: 13, marginBottom: 2 }}>
                      {item.label}
                    </div>
                    <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5 }}>
                      {item.sub}
                    </div>
                    {item.cta && (
                      <a href={item.cta.href} data-testid={item.step === "2" ? "cta-claim-station" : undefined}
                        style={{ display: "inline-block", marginTop: 8, color: C.bg,
                          background: C.cyan, padding: "6px 16px", borderRadius: 8,
                          fontWeight: 700, fontSize: 12, textDecoration: "none" }}>
                        {item.cta.label}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {Object.entries(byCountry).map(([country, list]) => (
            <div key={country} style={{ marginBottom: 28 }}>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>
                {country} · {list.length} stations
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10 }}>
                {list.map((s) => (
                  <div key={s.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{s.name}</div>
                    <div style={{ color: C.muted, fontSize: 11, marginBottom: 8 }}>
                      {s.frequency} · {s.city || s.country}
                      {s.is_verified && <span style={{ marginLeft: 6, color: C.cyan }}>✓</span>}
                      {!s.is_streaming && <span style={{ marginLeft: 6, color: "#F59E0B" }}>FM-only</span>}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button data-testid={`pick-${s.id}`} onClick={() => { setLoading(true); load(s.id); }}
                        style={{ flex: 1, padding: "7px 10px", background: "transparent", border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                        Preview
                      </button>
                      {process.env.NODE_ENV === "development" && process.env.REACT_APP_ENABLE_DEV_MOCK === "true" && (
                        <button data-testid={`mock-claim-${s.id}`} onClick={async () => {
                          try {
                            await api.post("/dev/mock-claim", { station_id: s.id });
                            setLoading(true); load(s.id);
                          } catch (e) { alert(e.response?.data?.detail || "Mock claim failed"); }
                        }}
                          title="Dev shortcut: instantly verify ownership of this station for testing"
                          style={{ flex: 1, padding: "7px 10px", background: C.cyan + "22", border: `1px solid ${C.cyan}55`, color: C.cyan, borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                          Mock claim
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="station-dashboard" style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Inter',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&family=Inter:wght@400;500;600&display=swap');
        @keyframes pulseG{0%{box-shadow:0 0 0 0 #10B98199}70%{box-shadow:0 0 0 8px #10B98100}100%{box-shadow:0 0 0 0 #10B98100}}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:${C.border};border-radius:4px}`}</style>
      <NavBar dark={true} />

      <div style={{ padding: "24px 32px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>STATION DASHBOARD</div>
            <h1 data-testid="station-name" style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700, color: C.text }}>
              {station.name}
              {station.is_verified && <span style={{ marginLeft: 8, color: C.cyan, fontSize: 14 }}>✓ Verified</span>}
            </h1>
            <div style={{ color: C.muted, fontSize: 12 }}>{station.frequency} · {station.city || station.country}</div>
            <a href={`/stations/${station.id}`} target="_blank" rel="noreferrer"
              data-testid="station-public-link"
              style={{ color: C.muted, fontSize: 11, textDecoration: "none",
                display: "inline-flex", alignItems: "center", gap: 3, marginTop: 4 }}>
              Public page ↗
            </a>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* Blueprint 6.5 — the killer "share with an advertiser" action */}
            <Button data-testid="generate-audience-report" size="md" variant="primary" audience="station"
              onClick={() => {
                const tid = toast.loading("Building your audience report…");
                api.get(`/stations/${station.id}/audience-report.pdf`, { responseType: "blob" })
                  .then((r) => {
                    const blob = new Blob([r.data], { type: "application/pdf" });
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = `audience-snapshot-${station.name.replace(/\s+/g, "_")}.pdf`;
                    document.body.appendChild(link); link.click(); link.remove();
                    toast.success("Audience report downloaded 📄", { id: tid, description: "Share it with any advertiser." });
                  })
                  .catch((e) => {
                    if (e.response?.status === 402) toast.error("Upgrade required", { id: tid, description: "Insights or Pro unlocks audience reports." });
                    else toast.error("Could not generate report", { id: tid, description: "Please try again." });
                  });
              }}>
              📄 Generate audience report
            </Button>
            <div style={{ display: "flex", gap: 8 }}>
              {TABS.map((t) => (
                <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                  style={{
                    padding: "8px 16px", borderRadius: 8,
                    border: `1px solid ${tab === t.id ? C.cyan + "44" : C.border}`,
                    background: tab === t.id ? C.cyanDim : "transparent",
                    color: tab === t.id ? C.cyan : C.muted,
                    cursor: "pointer", fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
                  }}
                >{t.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <FirstRunCard stationId={station?.id} />
            <OnboardingWidget onboarding={onboarding} setTab={setTab} />
            <MethodologyBanner source={live?.confidence_label || "MODEL"} />
            <MatrixActivity height={140} />
            <LiveCounter value={total} isOnline={live?.is_online} source={live?.confidence_label} loading={!live} />

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
              {[
                { label: "Online Connections", value: (live?.raw_connections || 0).toLocaleString(), sub: "Devices connected", accent: C.cyan, testid: "stat-connections" },
                { label: "Est. Total Listeners", value: total.toLocaleString(), sub: "FM + online combined", accent: "#10B981", testid: "stat-total", showPanelBadge: true },
                { label: "Est. Male", value: `${Math.round((gender.male?.pct || 0) * 100)}%`, sub: `${(gender.male?.count || 0).toLocaleString()} people`, accent: "#3B82F6", testid: "stat-male" },
                { label: "Est. Female", value: `${Math.round((gender.female?.pct || 0) * 100)}%`, sub: `${(gender.female?.count || 0).toLocaleString()} people`, accent: "#EC4899", testid: "stat-female" },
              ].map((card, i) => (
                <div key={i} data-testid={card.testid} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${card.accent},transparent)` }} />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 8 }}>
                    <div style={{ color: C.muted, fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>{card.label}</div>
                    {card.showPanelBadge && isPanelLatest && (
                      <span data-testid="panel-badge"
                        title={`USSD listener panel · ${latestSnap.panel_responses ?? 0} responses · ±${latestSnap.margin_of_error_pct ?? 0}%`}
                        style={{ background: PANEL_AMBER + "22", color: PANEL_AMBER, border: `1px solid ${PANEL_AMBER}55`,
                          fontSize: 8, fontWeight: 800, letterSpacing: "0.08em", padding: "2px 6px", borderRadius: 4, cursor: "help" }}>
                        PANEL
                      </span>
                    )}
                  </div>
                  <div style={{ color: C.text, fontSize: 28, fontWeight: 700, fontFamily: "'Space Grotesk',sans-serif", lineHeight: 1 }}>{card.value}</div>
                  <div style={{ color: C.muted, fontSize: 11, marginTop: 5 }}>{card.sub}</div>
                  {card.showPanelBadge && isPanelLatest && latestSnap.confidence_interval && (
                    <div data-testid="panel-ci-subtext"
                      style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`,
                        color: PANEL_AMBER, fontSize: 10, lineHeight: 1.5 }}>
                      Based on {(latestSnap.panel_responses ?? 0).toLocaleString()} panel responses · ±{latestSnap.margin_of_error_pct}%
                      <div style={{ color: C.muted, fontSize: 9, marginTop: 2 }}>
                        95% CI: {latestSnap.confidence_interval.lower.toLocaleString()}–{latestSnap.confidence_interval.upper.toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {history.length > 0 && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
                <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Listeners — Last 24 Hours</div>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={history} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={C.cyan} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={C.cyan} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gPanel" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={PANEL_AMBER} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={PANEL_AMBER} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis dataKey="label" stroke={C.dim} tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} interval={Math.max(1, Math.floor(history.length / 8))} />
                    <YAxis stroke={C.dim} tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} width={55} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                    <Tooltip content={<Tip />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: C.muted }} />
                    <Area type="monotone" dataKey="people" name="Est. Total People" stroke={C.cyan} strokeWidth={2} fill="url(#gC)" dot={false} connectNulls={false} />
                    <Area type="monotone" dataKey="connections" name="Online Connections" stroke="#10B981" strokeWidth={1.5} fill="none" dot={false} connectNulls={false} />
                    <Area type="monotone" dataKey="panel" name="USSD Panel (weighted)" stroke={PANEL_AMBER} strokeWidth={2.5} fill="url(#gPanel)" dot={{ fill: PANEL_AMBER, r: 3 }} connectNulls={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {live?.now_playing && (
              <div data-testid="now-playing" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
                <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>NOW PLAYING</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.cyan, animation: "pulseG 1.5s ease-out infinite", flexShrink: 0 }} />
                  <span style={{ color: C.text, fontSize: 16, fontWeight: 600 }}>{live.now_playing}</span>
                  <span style={{ color: C.muted, fontSize: 12, marginLeft: 4 }}>{live.content_type}</span>
                </div>
              </div>
            )}

            {/* Embed widget teaser */}
            <div data-testid="embed-teaser"
              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 20px",
                display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 700, marginBottom: 3 }}>
                  Put your live listener count on your website
                </div>
                <div style={{ color: C.muted, fontSize: 12 }}>
                  Free embeddable widget — auto-refreshes every 60 seconds with the WaveScorePro VERIFIED badge.
                </div>
              </div>
              <button onClick={() => setTab("embed")}
                style={{ padding: "8px 18px", background: C.cyanDim, border: `1px solid ${C.cyan}55`,
                  color: C.cyan, borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700,
                  whiteSpace: "nowrap", flexShrink: 0 }}>
                Get embed code →
              </button>
            </div>
          </div>
        )}

        {/* AUDIENCE */}
        {tab === "audience" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }} data-testid="gender-card">
                <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
                  Gender Split <ConfidenceLabel source={live?.confidence_label || "MODEL"} size="xs" />
                </div>
                {[
                  { label: "Male", pct: gender.male?.pct || 0, count: gender.male?.count || 0, color: "#3B82F6" },
                  { label: "Female", pct: gender.female?.pct || 0, count: gender.female?.count || 0, color: "#EC4899" },
                ].map((g, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <span style={{ color: C.text, fontSize: 13 }}>{g.label}</span>
                      <span style={{ color: g.color, fontSize: 14, fontWeight: 700, fontFamily: "'Space Grotesk',sans-serif" }}>
                        {Math.round(g.pct * 100)}% <span style={{ color: C.muted, fontSize: 11, fontWeight: 400 }}>({g.count.toLocaleString()})</span>
                      </span>
                    </div>
                    <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${g.pct * 100}%`, height: "100%", background: g.color, borderRadius: 4 }} />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }} data-testid="age-card">
                <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Age Breakdown</div>
                {AGE_BANDS.map((b) => {
                  const d = age[b.key] || { pct: 0, count: 0 };
                  return (
                    <div key={b.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <span style={{ color: C.muted, fontSize: 11, width: 36, flexShrink: 0 }}>{b.label}</span>
                      <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${(d.pct || 0) * 100}%`, height: "100%", background: b.color, borderRadius: 3 }} />
                      </div>
                      <span style={{ color: b.color, fontSize: 12, fontWeight: 600, width: 60, textAlign: "right" }}>{Math.round((d.pct || 0) * 100)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }} data-testid="lsm-card">
              <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>LSM (Living Standards Measure)</div>
              <div style={{ color: C.muted, fontSize: 11, marginBottom: 16 }}>
                Based on GSS 2021 PHC + GLSS-7 household asset data. Anchored to {station.region_code || "GH-AA"} baseline.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 12 }}>
                {LSM_BANDS.map((band) => {
                  const d = lsm[band.key] || { pct: 0, count: 0 };
                  return (
                    <div key={band.key} data-testid={`lsm-${band.key}`} style={{ textAlign: "center", background: C.surface, borderRadius: 8, padding: "14px 8px", border: `1px solid ${C.border}` }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: band.color, margin: "0 auto 8px" }} />
                      <div style={{ color: band.color, fontSize: 22, fontWeight: 800, fontFamily: "'Space Grotesk',sans-serif" }}>{Math.round((d.pct || 0) * 100)}%</div>
                      <div style={{ color: C.text, fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{band.label}</div>
                      <div style={{ color: C.muted, fontSize: 10 }}>{band.desc}</div>
                      <div style={{ color: C.muted, fontSize: 10, marginTop: 4 }}>{(d.count || 0).toLocaleString()}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gender x Age matrix */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }} data-testid="gender-age-matrix">
              <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Gender × Age Matrix</div>
              <table style={{ width: "100%", borderCollapse: "collapse", color: C.text, fontSize: 12 }}>
                <thead>
                  <tr style={{ color: C.muted, textAlign: "left" }}>
                    <th style={{ padding: "8px 6px", borderBottom: `1px solid ${C.border}` }}>Age</th>
                    {AGE_BANDS.map((b) => (
                      <th key={b.key} style={{ padding: "8px 6px", borderBottom: `1px solid ${C.border}` }}>{b.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[{ key: "male", label: "Male", color: "#3B82F6" }, { key: "female", label: "Female", color: "#EC4899" }].map((g) => (
                    <tr key={g.key}>
                      <td style={{ padding: "10px 6px", color: g.color, fontWeight: 700 }}>{g.label}</td>
                      {AGE_BANDS.map((b) => {
                        const ageCount = age[b.key]?.count || 0;
                        const genderPct = gender[g.key]?.pct || 0.5;
                        const cell = Math.round(ageCount * genderPct);
                        return <td key={b.key} style={{ padding: "10px 6px", borderTop: `1px solid ${C.border}` }}>{cell.toLocaleString()}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* MARKET */}
        {tab === "market" && (
          <div data-testid="market-tab" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
                <div style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>Ghana Market Rankings — Live</div>
                <div style={{ display: "flex", gap: 14, fontSize: 10, color: C.muted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: C.cyan, marginRight: 5 }} />Live stream</span>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: PANEL_AMBER, marginRight: 5 }} />USSD Panel</span>
                </div>
              </div>
              {/* Column header */}
              {rankings.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 12px",
                  color: C.muted, fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                  borderBottom: `1px solid ${C.border}`, marginBottom: 6 }}>
                  <div style={{ width: 26, flexShrink: 0 }}>#</div>
                  <div style={{ flex: 1, minWidth: 0 }}>Station</div>
                  <div style={{ width: 120, flexShrink: 0 }}>Share</div>
                  <div style={{ width: 72, flexShrink: 0, textAlign: "center" }} title="USSD panel sample size for PANEL rows. 'Live' = direct stream signal.">
                    Sample
                  </div>
                  <div style={{ minWidth: 90, flexShrink: 0, textAlign: "right" }}>Listeners</div>
                </div>
              )}
              {rankings.map((r, i) => {
                const isYou = r.station_id === station.id;
                const maxV = rankings[0]?.estimated_total_people || 1;
                const isPanel = r.data_source === "PANEL" || r.confidence_label === "PANEL" || r.panel_responses != null;
                const ci = r.confidence_interval;
                const total = r.estimated_total_people || 0;
                const ciTooltip = ci
                  ? `95% CI: ${ci.lower.toLocaleString()}–${ci.upper.toLocaleString()} listeners (±${r.margin_of_error_pct}%)`
                  : isPanel
                    ? `USSD panel · ${r.panel_responses ?? 0} responses`
                    : `Live stream signal (${r.confidence_label || r.data_source || "—"})`;
                return (
                  <div key={r.station_id} data-testid={`ranking-row-${r.rank}`}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "11px 12px", borderRadius: 8, marginBottom: 4,
                      background: isYou ? C.cyanDim : i % 2 === 0 ? C.surface : "transparent",
                      border: isYou ? `1px solid ${C.cyan}33` : "1px solid transparent",
                    }}
                  >
                    <div style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: isYou ? C.cyan : C.border, color: isYou ? C.bg : C.muted,
                      fontSize: 11, fontWeight: 700 }}>{r.rank}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ color: isYou ? C.cyan : C.text, fontWeight: isYou ? 700 : 400, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
                        {isYou && <span style={{ background: C.cyan, color: C.bg, fontSize: 8, fontWeight: 800, padding: "1px 5px", borderRadius: 3, flexShrink: 0 }}>YOU</span>}
                        {r.confidence_label && <ConfidenceLabel source={r.confidence_label} size="xs" />}
                      </div>
                    </div>
                    <div style={{ width: 120, flexShrink: 0 }}>
                      <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ width: `${(total / maxV) * 100}%`, height: "100%",
                          background: isPanel ? PANEL_AMBER : (isYou ? C.cyan : C.dim), borderRadius: 2 }} />
                      </div>
                    </div>
                    {/* Sample column — PANEL responses count, or "Live" for stream */}
                    <div data-testid={`ranking-sample-${r.rank}`}
                      title={isPanel
                        ? `USSD panel · ${(r.panel_responses ?? 0).toLocaleString()} weighted responses · ±${r.margin_of_error_pct ?? "—"}%`
                        : "Direct stream signal — listener count from broadcaster's live Icecast/SHOUTcast feed"}
                      style={{ width: 72, flexShrink: 0, textAlign: "center", cursor: "help" }}>
                      {isPanel ? (
                        <>
                          <div style={{ color: PANEL_AMBER, fontWeight: 700, fontSize: 12, fontFamily: "'Space Grotesk',sans-serif" }}>
                            {(r.panel_responses ?? 0).toLocaleString()}
                          </div>
                          <div style={{ color: C.muted, fontSize: 9, marginTop: 1 }}>panel</div>
                        </>
                      ) : (
                        <>
                          <div style={{ color: C.cyan, fontWeight: 700, fontSize: 11, letterSpacing: "0.06em" }}>LIVE</div>
                          <div style={{ color: C.muted, fontSize: 9, marginTop: 1 }}>stream</div>
                        </>
                      )}
                    </div>
                    <div title={ciTooltip} style={{ textAlign: "right", minWidth: 90, flexShrink: 0, cursor: "help" }}>
                      <div style={{ color: isYou ? C.cyan : C.text, fontWeight: 600, fontFamily: "'Space Grotesk',sans-serif", fontSize: 15,
                        display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {total.toLocaleString()}
                        {ci && (
                          <span style={{ color: PANEL_AMBER, fontSize: 10, fontWeight: 700 }}
                            data-testid={`ranking-ci-marker-${r.rank}`}>±</span>
                        )}
                      </div>
                      <div style={{ color: C.muted, fontSize: 10 }}>est. people</div>
                    </div>
                  </div>
                );
              })}
              {rankings.length === 0 && <div style={{ color: C.muted, fontSize: 14, textAlign: "center", padding: "20px 0" }}>Rankings load after first crawl cycle.</div>}
            </div>
          </div>
        )}

        {/* EMBED */}
        {tab === "embed" && (
          <EmbedTab station={station} apiBase={API_BASE} />
        )}

        {/* RATE CARD — data capture */}
        {tab === "rate_card" && (
          <RateCardTab station={station} live={live}
            onSaved={() => refreshOnboarding(station.id)} />
        )}

        {/* SCHEDULE — data capture */}
        {tab === "schedule" && (
          <ScheduleTab station={station}
            onSaved={() => refreshOnboarding(station.id)} />
        )}

        {/* STATION PROFILE — data capture */}
        {tab === "profile" && (
          <ProfileTab station={station}
            onSaved={() => { refreshOnboarding(station.id); load(station.id); }} />
        )}

        {/* AD LOG — CSV upload → PANEL certificates */}
        {tab === "ad_log" && (
          <AdLogTab station={station}
            onSaved={() => refreshOnboarding(station.id)} />
        )}
      </div>
    </div>
  );
}

function EmbedTab({ station, apiBase }) {
  const iframeUrl = `${apiBase}/embed/${station.id}`;
  const [theme, setTheme] = useState("light");
  const themedUrl = theme === "dark" ? `${iframeUrl}?theme=dark` : iframeUrl;
  const snippet = `<iframe src="${themedUrl}" width="320" height="170" frameborder="0" scrolling="no" style="border:0;border-radius:12px;overflow:hidden"></iframe>`;
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(snippet);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div data-testid="embed-tab" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
        <div style={{ color: C.text, fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Share your live audience widget</div>
        <div style={{ color: C.muted, fontSize: 13, marginBottom: 18, lineHeight: 1.6 }}>
          Drop this widget on your station's homepage. It shows your live listener count
          with the WaveScorePro VERIFIED badge — instant credibility and free promotion.
          It auto-refreshes every 60 seconds.
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {["light", "dark"].map((t) => (
            <button key={t} data-testid={`theme-${t}`} onClick={() => setTheme(t)}
              style={{ padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                background: theme === t ? C.cyanDim : "transparent",
                border: `1px solid ${theme === t ? C.cyan + "55" : C.border}`,
                color: theme === t ? C.cyan : C.muted, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20, marginBottom: 20 }}>
          <div>
            <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Live Preview</div>
            <iframe data-testid="embed-preview" title="widget-preview" src={themedUrl}
              width="320" height="170" frameBorder="0" scrolling="no"
              style={{ border: 0, borderRadius: 12, overflow: "hidden", background: theme === "dark" ? "#0A1628" : "#fff" }} />
          </div>
          <div>
            <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Copy & paste this HTML</div>
            <textarea data-testid="embed-snippet" readOnly value={snippet}
              style={{ width: "100%", minHeight: 100, padding: 14, borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontFamily: "'DM Mono',monospace", fontSize: 12, resize: "vertical" }} />
            <button data-testid="embed-copy" onClick={copy}
              style={{ marginTop: 10, padding: "10px 20px", background: copied ? C.cyan : "transparent", color: copied ? C.bg : C.cyan,
                border: `1px solid ${C.cyan}66`, borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
              {copied ? "✓ Copied" : "Copy snippet"}
            </button>
          </div>
        </div>

        <div style={{ padding: "12px 16px", background: C.surface, borderRadius: 8, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Direct JSON endpoint (for custom integrations)</div>
          <code style={{ color: C.cyan, fontFamily: "'DM Mono',monospace", fontSize: 12, wordBreak: "break-all" }}>{apiBase}/embed/{station.id}.json</code>
        </div>
      </div>
    </div>
  );
}
